import React, { useMemo, useState, useEffect } from "react";
import { Box, Typography, Button, Tabs, Tab, Chip, Switch, FormControlLabel, Menu, MenuItem } from "@mui/material";
import CustomAvatar from "../../../shared/UIParts/CustomAvatar";
import EditableTextField from "../../../shared/UIParts/EditableTextField";
import { User, UserPrivacyData } from "../../../interfaces/DTO/user";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Phone";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { Family } from "../../../interfaces/DTO/crm/family";
import {
  Mimshak,
  MaritalStatus,
} from "../../../interfaces/DTO/mislaka_xml_models";

import { calculateAge } from "../../../utils/dateCalculators";
import { formatUniversalPhone } from "../../../utils/namesFormater";
import {
  formatAge,
  formatIsoDate,
  formatName,
} from "../../../utils/namesFormater";
import {
  formatMaritalStatus,
} from "../../../utils/enumFormatter";

import { EditableField, EditableFieldsBlock } from "../../../shared/EditableFieldsBlock";
import { IconButton, Tooltip, ListItemIcon } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Contact, ContactStatus } from "../../../interfaces/DTO/crm/contact";
import { formatContactStatus } from "../../../utils/featuresFormatter";
import { BeneficiaryInfo } from "../../../interfaces/DTO/mislaka_xml_insurence_models";
import { usePartialUpdateContact } from "../../../hooks/crm/useContacts";
import { calculateAvatarStyle, AVATAR_COLOR_REV_CLIENT } from "../../../utils/avatarStyleCalculator";
import GeneratePublicLink from "../../Users/GeneratePublicLink";
import ContactBeneficiariesTab from "./beneficiaries/ContactBeneficiariesTab";
import ContactBankAccountsTab from "./bankAccounts/ContactBankAccountsTab";
import { useContactSpouse } from "../../../hooks/crm/useContactSpouse";
import ContactEmploymentSection from "./employment/ContactEmploymentSection";
import FundEmploymentInfo from "./employment/FundEmploymentInfo";
import CitizenshipCard from "./CitizenshipCard";
import ContactAddressCard from "./ContactAddressCard";
import EmploymentStatusSelect from "./EmploymentStatusSelect";
import HeverMembershipCard from "./HeverMembershipCard";
import { withDataField, withNestedDataField } from "../../../utils/contactDataUtil";

interface ContactInfoProps {
  contact: Contact;

  family?: Family;
  mimshaks: Mimshak[];
}

export const ContactInfo: React.FC<ContactInfoProps> = ({
  contact: contact,

  family,
  mimshaks,
}) => {
  const [editedName, setEditedName] = useState(formatName(contact));
  const [editedCellular, setEditedCellular] = useState(contact.phone || "");
  const [activeTab, setActiveTab] = useState(0);
  const [editingChildIndex, setEditingChildIndex] = useState<number | null>(null);

  const [showRevSwitch, setShowRevSwitch] = useState(false);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);

  const updateContact = usePartialUpdateContact();
  // the spouse - offered with the children as beneficiaries
  const spouse = useContactSpouse(contact.id, family?.id);


  // המרת Mimshak[] ל-MimshakData[] לצורך חישוב שכר בסיס
  const mimshakData = useMemo(() => {
    return mimshaks.flatMap((mimshak, mimshakIndex) =>
      (mimshak.products || []).map((product, productIndex) => ({
        id: mimshakIndex * 1000 + productIndex,
        contact_id: contact.id!,
        handler_code: product.details?.handler_code || "",
        reported_period: "",
        product_type: product.details?.product_type || -1,
        funds_sum: 0,
        data: mimshak,
        policy_number: product.policies_or_accounts?.[0]?.policy_number || "",
        deposit_fee: 0,
        saving_fee: 0,
        deposit_amount: 0,
        withdrawal_amount: 0,
        new_policy: false,
      }))
    );
  }, [mimshaks, contact.id]);



  const editableFields: EditableField[] = [
    {
      field: "email",
      headerName: "אימייל",
      value: contact.email || "",
      type: "email",
      required: true,
      editable: true,
    },

    {
      field: "israeli_id",
      headerName: "מספר תעודת זהות",
      value: contact.israeli_id || "",
      type: "text",
      editable: false,
    },
   
    {
      field: "date_of_birth",
      headerName: "תאריך לידה",
      value: contact.date_of_birth || "",
      type: "date",
      editable: true,
      valueFormatter: (v: string) => formatIsoDate(v),
    },
     {
      field: "age",
      headerName: "גיל",
      value: formatAge(contact.date_of_birth) || "",
      type: "text",
      editable: false,
    },
    {
      field: "marital_status",
      headerName: "מצב משפחתי",
      value: (contact.marital_status ?? "").toString(),
      type: "select",
      editable: true,
      options: Object.values(MaritalStatus)
        .filter((value) => typeof value === "number")
        .map((value) => ({
          value: value.toString(),
          label: formatMaritalStatus(value as MaritalStatus),
        })),
      valueFormatter: (v: string) => formatMaritalStatus(Number(v)),
    },
    {
      field: "children_count",
      headerName: "מספר ילדים",
      value: contact.children_count?.toString() || "0",
      type: "number",
      editable: true,
    },
  ];








  const getChildFields = (child: BeneficiaryInfo, index: number): EditableField[] => [
    {
      field: `first_name_${index}`,
      headerName: "שם פרטי",
      value: child.first_name || "",
      type: "text" as const,
      editable: true,
    },
    {
      field: `last_name_${index}`,
      headerName: "שם משפחה",
      value: child.last_name || "",
      type: "text" as const,
      editable: true,
    },
    {
      field: `id_number_${index}`,
      headerName: "ת.ז",
      value: child.id_number || "",
      type: "text" as const,
      editable: true,
    },
    {
      field: `birth_date_${index}`,
      headerName: "תאריך לידה",
      value: child.birth_date || "",
      type: "date" as const,
      editable: true,
      valueFormatter: (v: string) => formatIsoDate(v),
    },
  ];

  // Functions to handle children data array
  const handleAddChild = () => {
    const currentChildren = contact.data?.family_data || [];
    setEditingChildIndex(currentChildren.length);
    const newChild: BeneficiaryInfo = {
      identification_type: 1, // ברירת מחדל: תעודת זהות
      id_code: 1, // ברירת מחדל: תעודת זהות ישראלית
      first_name: "",
      last_name: "",
      id_number: "",
      birth_date: "",
    };
    updateContact.mutate({
      id: contact.id!,
      fields: {
        data: {
          ...contact.data,
          family_data: [...currentChildren, newChild],
        },
      },
    });
  };

  const handleDeleteChild = (index: number) => {
    const currentChildren = contact.data?.family_data || [];
    const updatedChildren = currentChildren.filter((_, i) => i !== index);
    updateContact.mutate({
      id: contact.id!,
      fields: {
        data: {
          ...contact.data,
          family_data: updatedChildren,
        },
      },
    });
  };

  const handleUpdateChild = (index: number, field: keyof BeneficiaryInfo, value: any) => {
    const currentChildren = contact.data?.family_data || [];
    const updatedChildren = [...currentChildren];

    let processedValue = value;
    if (value === "" || value === null) {
      processedValue = undefined;
    }

    updatedChildren[index] = {
      ...updatedChildren[index],
      [field]: processedValue,
    };

    updateContact.mutate({
      id: contact.id!,
      fields: {
        data: {
          ...contact.data,
          family_data: updatedChildren,
        },
      },
    });
  };





  const formalFamilyRelationsFields: EditableField[] = [
    {
      field: "data.formal_relations.is_public_figure",
      headerName: "איש/אשת ציבור?",
      value: contact.data?.formal_relations?.is_public_figure === true ? "1" : contact.data?.formal_relations?.is_public_figure === false ? "0" : "",
      type: "select" as const,
      editable: true,
      options: [
        { value: "", label: "" },
        { value: "1", label: "כן" },
        { value: "0", label: "לא" },
      ],
      valueFormatter: (v: string) => (v === "1" ? "כן" : v === "0" ? "לא" : ""),
    },
    {
      field: "data.formal_relations.family_senior_position",
      headerName: "בן משפחה כיהן בתפקיד ציבורי בכיר ב-5 השנים האחרונות?",
      value: contact.data?.formal_relations?.family_senior_position === true ? "1" : contact.data?.formal_relations?.family_senior_position === false ? "0" : "",
      type: "select" as const,
      editable: true,
      options: [
        { value: "", label: "" },
        { value: "1", label: "כן" },
        { value: "0", label: "לא" },
      ],
      valueFormatter: (v: string) => (v === "1" ? "כן" : v === "0" ? "לא" : ""),
    },
    {
      field: "data.formal_relations.relation_or_position",
      headerName: "קשר משפחתי/תפקיד",
      value: contact.data?.formal_relations?.relation_or_position || "",
      type: "text" as const,
      editable: true,
    },
    {
      field: "data.formal_relations.aml_service_refused",
      headerName: "סירוב שירות בשל הלבנת הון/מימון טרור?",
      value: contact.data?.formal_relations?.aml_service_refused === true ? "1" : contact.data?.formal_relations?.aml_service_refused === false ? "0" : "",
      type: "select" as const,
      editable: true,
      options: [
        { value: "", label: "" },
        { value: "1", label: "כן" },
        { value: "0", label: "לא" },
      ],
      valueFormatter: (v: string) => (v === "1" ? "כן" : v === "0" ? "לא" : ""),
    },
  ];

// Handler for nested data fields
const handleNestedDataChange = (field: string, value: any) => {
  let update: any;

  // Handle all nested data fields
  if (field.startsWith("data.")) {
    const parts = field.split(".");
    const nestedObject = parts[1]; // biz_data, last_employment_data, residential_data, formal_family_relations
    const nestedField = parts[2];

    // Type conversion for specific fields
    let processedValue = value;

    // Check if this is a boolean field
    const booleanFields = [
      "foreign_resident", "additional_country_resident", "us_citizenship",
      "is_public_figure", "family_senior_position", "aml_service_refused"
    ];

    if (value === "" || value === null) {
      // Empty value should be null for optional fields
      processedValue = null;
    } else if (nestedField === "annual_revenue") {
      processedValue = parseFloat(value as string);
    } else if (booleanFields.includes(nestedField)) {
      // Convert "1"/"0" strings to boolean for boolean fields
      if (value === "1") {
        processedValue = true;
      } else if (value === "0") {
        processedValue = false;
      } else {
        processedValue = null;
      }
    }

    // the whole data (a partial update replaces it as is), with only the filled in fields
    if (nestedField === undefined) {
      // 2-part path: data.fieldName — direct field on contact.data
      update = { data: withDataField(contact.data, nestedObject, processedValue) };
    } else {
      update = { data: withNestedDataField(contact.data, nestedObject, nestedField, processedValue) };
    }
  } else {
    // Handle non-nested fields like has_hever
    let processedValue = value;
    if (field === "has_hever") {
      if (value === "1") {
        processedValue = true;
      } else if (value === "0") {
        processedValue = false;
      } else {
        processedValue = null;
      }
    }
    update = { [field]: processedValue };
  }

  updateContact.mutate({ id: contact.id!, fields: update });
};

const handleSaveUser = async (name: string) => {
  try {
     const nameParts = name.trim().split(/\s+/);
    const updatedUser: Partial<User> = {
      first_name: nameParts[0] || "",
      last_name: nameParts.slice(1).join(" ") || "",
    };
    await updateContact.mutateAsync({ id: contact.id!, fields: updatedUser });
  } catch (error) {
    console.error("Error updating user:", error);
  }
};

const handleSavePrivacy = async (cellular: string) => {
  try {
    const updatedPrivacy: Partial<Contact> = {
      phone: cellular,
    };
    await updateContact.mutateAsync({ id: contact.id!, fields: updatedPrivacy });
  } catch (error) {
    console.error("Error updating user privacy:", error);
  }
};
const handleCopyNameWithId = () => {
  const textToCopy = `${editedName} ת.ז: ${contact.israeli_id || ""}`;
  navigator.clipboard.writeText(textToCopy).then(() => {

  });
};
const handleGmailLink = () => {
 const subject = encodeURIComponent(`${editedName} ת.ז: ${contact.israeli_id || ""}`);
window.open(`https://mail.google.com/mail/?view=cm&fs=1&tf=1&su=${subject}`, "_blank");
};
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        p: 2,
        borderRadius: "8px",
        backgroundColor: "background.paper",
        boxShadow: 1,
        width: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CustomAvatar
          item={contact}
          isUser={true}
          userColor={calculateAvatarStyle(contact, true).backgroundColor}
          familyColor=""
          sx={{ width: 200, height: 200, fontSize: "3rem", cursor: "pointer" }}
          onClick={() => setShowRevSwitch((v) => !v)}
        />
        <Menu
          anchorEl={statusMenuAnchor}
          open={!!statusMenuAnchor}
          onClose={() => setStatusMenuAnchor(null)}
        >
          {Object.values(ContactStatus)
            .filter((value) => value !== ContactStatus.DELETED)
            .map((value) => (
              <MenuItem
                key={value}
                selected={contact.status === value}
                onClick={() => {
                  updateContact.mutate({ id: contact.id!, fields: { status: value } });
                  setStatusMenuAnchor(null);
                }}
                sx={{ direction: "rtl" }}
              >
                {contact.status === value && (
                  <ListItemIcon>
                    <CheckIcon fontSize="small" />
                  </ListItemIcon>
                )}
                {formatContactStatus(value)}
              </MenuItem>
            ))}
        </Menu>
      </Box>
      {contact.rev_client === true && (
        <Chip
          label="לקוח REV&U"
          size="small"
          sx={{ backgroundColor: AVATAR_COLOR_REV_CLIENT, color: "#fff", fontWeight: 700 }}
        />
      )}
      {showRevSwitch && (
        <FormControlLabel
          control={
            <Switch
              checked={contact.rev_client === true}
              onChange={(e) => {
                updateContact.mutate({ id: contact.id!, fields: { rev_client: e.target.checked } });
                setShowRevSwitch(false);
              }}
              sx={{ "& .MuiSwitch-thumb": { backgroundColor: AVATAR_COLOR_REV_CLIENT } }}
            />
          }
          label="לקוח REV&U"
        />
      )}

    {/* the name centered under the avatar; copy / email / WhatsApp at the left edge */}
    <Box sx={{ position: "relative", width: "100%", display: "flex", justifyContent: "center", px: 18 }}>
      <EditableTextField
        showEditOnHover
        bold
        fontSize="1.75rem"
        value={editedName}
        onSave={(newValue) => {
          setEditedName(newValue);
          handleSaveUser(newValue);
        }}
        isLoading={updateContact.status === "pending"}
      />
      <Box sx={{ position: "absolute", left: 0, top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center" }}>
        <Tooltip title="העתק שם + ת.ז">
          <IconButton onClick={handleCopyNameWithId}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="שלח אימייל עם נושא כולל שם ות.ז">
          <IconButton onClick={handleGmailLink}>
            <EmailIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={contact.phone ? "שלח וואטסאפ ללקוח" : "לא הוזן טלפון"}>
          <span>
            <IconButton
              disabled={!contact.phone}
              onClick={() => window.open(`https://wa.me/${formatUniversalPhone(contact.phone!)}`, "_blank")}
            >
              <WhatsAppIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
    {/* the phone centered; the contact's status at the left edge */}
    <Box sx={{ position: "relative", width: "100%", display: "flex", justifyContent: "center", px: 18 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <PhoneIcon fontSize="small" color="action" />
        <EditableTextField
          showEditOnHover
          value={editedCellular}
          onSave={(newValue) => {
            setEditedCellular(newValue);
            handleSavePrivacy(newValue);
          }}
          isLoading={updateContact.status === "pending"}
        />
      </Box>
      {contact.status && (
        <Chip
          label={formatContactStatus(contact.status)}
          size="small"
          onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
          sx={{ cursor: "pointer", position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)" }}
        />
      )}
    </Box>

    <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto" sx={{ width: "100%", mb: 1 }}>
      <Tab label="פרטים" />
      <Tab label={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          ילדים
          {(contact.data?.family_data?.length || 0) > 0 && (
            <Box sx={{
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              borderRadius: "50%",
              width: 18,
              height: 18,
              fontSize: "0.65rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}>
              {contact.data.family_data!.length}
            </Box>
          )}
        </Box>
      } />
      <Tab label="מידע נוסף" />
      <Tab label="מוטבים" />
      <Tab label="חשבונות בנק" />
    </Tabs>

    {activeTab === 0 && (<>
    <EditableFieldsBlock
      title="פרטים אישיים"
      fields={editableFields}
      onChange={(field, value) => {
        const update = { [field]: value };



          updateContact.mutate({ id: contact.id!, fields: update });

      }}
    />
    <ContactAddressCard contact={contact} />
    {/* citizenship - compact ("אזרח ישראלי בלבד" by default); חבר בחבר - its own card */}
    <CitizenshipCard contact={contact} />
    <HeverMembershipCard contact={contact} />

    {/* the employment status, then the last employment (employee / self employed / company) - shared with the consulting drawer */}
    <Box sx={{ width: "100%", mt: 2 }}>
      <EmploymentStatusSelect contact={contact} />
    </Box>
    <ContactEmploymentSection contact={contact} />

      {/* the latest employer in the funds (click - add it to the last employment) and the occupations */}
      <FundEmploymentInfo contact={contact} mimshakData={mimshakData} />

      {(updateContact.isError || updateContact.isError) && (
        <Typography color="error">
          שגיאה בעדכון הנתונים: {updateContact.error?.message }
        </Typography>
      )}
    </>)}

    {activeTab === 1 && (
      <Box sx={{ width: "100%", mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="h6">ילדים</Typography>
          <Button startIcon={<AddIcon sx={{ ml: 1 }} />} color="secondary" onClick={handleAddChild} size="small">
            הוסף ילד
          </Button>
        </Box>
        {(contact.data?.family_data || []).map((child, index) => (
          <Box key={index} sx={{ mb: 1, backgroundColor: "#fff", borderRadius: 2, p: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {child.first_name || `ילד ${index + 1}`}
                {child.birth_date ? ` · גיל ${calculateAge(child.birth_date)}` : ""}
              </Typography>
              <IconButton onClick={() => handleDeleteChild(index)} size="small">
                <DeleteIcon />
              </IconButton>
            </Box>
            <EditableFieldsBlock
              title=""
              fields={getChildFields(child, index)}
              onChange={(field, value) => {
                const fieldName = field.replace(`_${index}`, "") as keyof BeneficiaryInfo;
                handleUpdateChild(index, fieldName, value);
              }}
              initialEditMode={editingChildIndex === index}
              onEditModeChange={(mode) => { if (!mode && editingChildIndex === index) setEditingChildIndex(null); }}
              noPadding
            />
          </Box>
        ))}
      </Box>
    )}

    {activeTab === 3 && <ContactBeneficiariesTab contact={contact} spouse={spouse} />}
    {activeTab === 4 && <ContactBankAccountsTab contact={contact} spouse={spouse} />}

    {activeTab === 2 && (<>
      <EditableFieldsBlock
        title="קשרים משפחתיים רשמיים וציות"
        fields={formalFamilyRelationsFields}
        onChange={handleNestedDataChange}
      />
    </>)}
    </Box>
  );
};
